import { Color } from 'src/generated/graphql';
import { RecurrenceOptions } from '../@types/recurrence';
import { WorkspaceParticipant } from '../@types/workspace-info';
import { TagItem, TagPayload } from '../components/Tags/types';
import { axios } from '../utils/axios';

export interface InfiniteData<TData> {
  pages: TData[];
  pageParams: unknown[];
}

export type GoogleCalendarTodo = {
  id: string;
  email: string;
};

export type TodoSublist = Omit<TodoList, 'subLists'>;

export type TodoList = {
  _id: string;
  name: string;
  workspace: string;
  owner: string;
  createdAt: string;
  updatedAt: string;
  googleCalendars: GoogleCalendarTodo[];
  isAssignedToMe?: boolean;
  list?: string;
  subLists: TodoSublist[];
  default?: boolean;
  order: number;
  icon?: string;
};

export type TodoItem = {
  id?: string;
  _id: string;
  title: string;
  description: string;
  completed: boolean;
  dueDate: string;
  tags: TagItem[];
  list: string;
  workspace: string;
  owner: string;
  createdAt: string;
  updatedAt: string;
  participants: WorkspaceParticipant[];
  googleEventId?: string;
  rrule?: string;
  rruleText?: string;
  recurrence?: RecurrenceOptions;
  order: number;
  color: Color;
};

export async function getTodoGroups() {
  const { data } = await axios.get<TodoList[]>('/todo/list');
  return data;
}

export async function createTodoGroup(name: string) {
  const { data } = await axios.post<TodoList>(`/todo/list/create`, {
    name,
  });
  return data;
}

export type TodoItemsByGroupObject = {
  list: string;
  start?: string;
  end?: string;
  hideCompleted?: boolean;
  skip?: number;
  limit?: number;
  q?: string;
  sort?: string;
};

export async function getTodoItemsByGroup(object: TodoItemsByGroupObject) {
  const { data } = await axios.get<TodoItem[]>(`/todo/${object.list}/todos`, {
    params: object,
  });
  return data;
}

export async function getTodoRecurrenceItems(object: TodoItemsByGroupObject) {
  const { data } = await axios.get<TodoItem[]>(
    `/todo/${object.list}/todos/recurrence`,
    {
      params: object,
    },
  );
  return data;
}

export type UpdateTodoGroupPayload = {
  id: string;
  name: string;
};

export async function updateTodoGroup(todo: UpdateTodoGroupPayload) {
  const { data } = await axios.put<TodoList>(`/todo/list/update`, todo);
  return data;
}

export async function deleteTodoGroup(id: string) {
  const { data } = await axios.delete(`/todo/list/delete?id=${id}`);
  return data;
}

export type UpdateCalendarTodoPayload = {
  id: string;
  email: string;
  calendarId: string;
};

export async function addCalendarToTodoList(object: UpdateCalendarTodoPayload) {
  const { data } = await axios.put<TodoList>(`/todo/list/calendar/add`, object);
  return data;
}

export type RemoveCalendarTodoPayload = {
  id: string;
  calendarId: string;
};

export async function removeCalendarFromTodoList(
  object: RemoveCalendarTodoPayload,
) {
  const { data } = await axios.delete(`/todo/list/calendar/delete`, {
    params: object,
  });
  return data;
}

export type CreateTodoItemPayload = {
  _id?: number;
  list: string;
  title: string;
  dueDate?: string;
  createdAt?: string;
  recurrence?: RecurrenceOptions;
};

export async function createTodo(newTodo: CreateTodoItemPayload) {
  const { data } = await axios.post<TodoItem>(`/todo/create`, {
    ...newTodo,
  });
  return data;
}

export async function updateTodo(todo: Partial<TodoItem>) {
  const { data } = await axios.put<TodoItem>(`/todo/update`, todo);
  return data;
}

export async function deleteTodo(id: string) {
  const { data } = await axios.delete(`/todo/delete?id=${id}`);
  return data;
}

export type TodoParticipantPayload = {
  id: string;
  participants: string[];
};

export async function addTodoParticipant(object: TodoParticipantPayload) {
  const { data } = await axios.put<TodoItem>(`/todo/participant/add`, object);
  return data;
}

export async function removeTodoParticipant(object: TodoParticipantPayload) {
  const { data } = await axios.put<TodoItem>(
    `/todo/participant/remove`,
    object,
  );
  return data;
}

export async function getTagsByTodoGroup(object: { list: string }) {
  const { data } = await axios.get<TagItem[]>(`/todo/tag`, { params: object });
  return data;
}

export async function addTodoTag(object: TagPayload) {
  const { data } = await axios.patch<TodoItem>(`/todo/tag/add`, object);
  return data;
}

export async function removeTodoTag(object: TagPayload) {
  const { data } = await axios.patch<TodoItem>(`/todo/tag/remove`, object);
  return data;
}

export type CreateTagPayload = {
  list: string;
  name: string;
  color: string;
};

export async function createTag(newTag: CreateTagPayload) {
  const { data } = await axios.post<TagItem>(`/todo/tag/create`, {
    ...newTag,
  });
  return data;
}

export async function updateTag(tag: Partial<TagItem> & Pick<TagItem, 'id'>) {
  const { data } = await axios.put<TodoItem>(`/todo/tag/update`, tag);
  return data;
}

export async function deleteTag(id: string) {
  const { data } = await axios.delete(`/todo/tag/delete?id=${id}`);
  return data;
}

export async function exportTodos(): Promise<void> {
  const { data } = await axios.get(`/todo/export`);
  return data;
}

export async function importTodos(
  file: File,
  onProgress?: (percentage: number, speed?: number) => void,
): Promise<Partial<TodoItem>> {
  const fd = new FormData();
  fd.append('file', file);

  const { data } = await axios.post<Partial<TodoItem>>(`/todo/import`, fd, {
    onUploadProgress: (progressEvent) => {
      onProgress?.(
        (progressEvent.loaded / (progressEvent.total || 0)) * 100,
        progressEvent.rate,
      );
    },
  });

  return data;
}

export * as TodosRequests from './todos';
